/**
 * Entitlements Debug Panel
 * ========================
 * Dev/admin-only panel to display exact entitlement truth for the current user.
 * 
 * Access Control:
 * - Only visible if NODE_ENV !== 'production' OR ENABLE_DEBUG_PANELS=true
 * - AND current user is admin
 * 
 * Features:
 * - Floating collapsible panel
 * - Read-only display of all entitlements
 * - User info, features, limits, thresholds, verification status
 */

import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../../App";
import { 
  Bug, 
  ChevronDown, 
  ChevronUp, 
  User, 
  Shield, 
  Gauge, 
  Settings,
  CheckCircle2,
  XCircle,
  RefreshCw,
  X,
  Minimize2,
  Maximize2,
  AlertTriangle
} from "lucide-react";

const API = window.location.origin;

// Check if debug panel should be enabled
const IS_DEV = process.env.NODE_ENV !== 'production';
const DEBUG_PANELS_ENABLED = process.env.REACT_APP_ENABLE_DEBUG_PANELS === 'true';
const PANEL_ALLOWED = IS_DEV || DEBUG_PANELS_ENABLED;

// Storage key for panel state
const PANEL_STATE_KEY = 'entitlements_debug_panel_state';

/**
 * Small toggle button to expand the panel
 */
function PanelToggleButton({ onClick, isVisible }) {
  if (isVisible) return null;
  
  return (
    <button
      onClick={onClick}
      className="fixed bottom-20 right-4 z-[10001] bg-slate-800 border border-slate-700 rounded-full p-3 shadow-lg hover:bg-slate-700 transition-all group"
      title="Open Entitlements Debug Panel"
      data-testid="entitlements-debug-toggle"
    >
      <Bug className="w-5 h-5 text-amber-400 group-hover:text-amber-300" />
    </button>
  );
}

/**
 * Boolean indicator with icon
 */
function BoolIndicator({ value, label }) {
  return (
    <div className="flex items-center justify-between py-1 px-2 rounded bg-slate-800/50">
      <span className="text-xs text-slate-400">{label}</span>
      {value ? (
        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
      ) : (
        <XCircle className="w-4 h-4 text-slate-500" />
      )}
    </div>
  );
}

/**
 * Numeric value display
 */
function NumericValue({ label, value, unit = "" }) {
  return (
    <div className="flex items-center justify-between py-1 px-2 rounded bg-slate-800/50">
      <span className="text-xs text-slate-400">{label}</span>
      <span className="text-xs font-mono text-slate-200">
        {value ?? "—"}{unit && ` ${unit}`}
      </span>
    </div>
  );
}

/**
 * Section header
 */
function Section({ icon: Icon, title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-slate-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 bg-slate-800 hover:bg-slate-750 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-slate-400" />
          <span className="text-xs font-medium text-slate-300">{title}</span>
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 text-slate-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-500" />
        )}
      </button>
      {open && (
        <div className="p-2 space-y-1 bg-slate-900/50">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Tier badge
 */
function TierBadge({ tier, isActive }) {
  const tierColors = {
    free: "bg-slate-600 text-slate-200",
    inner_circle: "bg-purple-600 text-purple-100",
    elite: "bg-amber-600 text-amber-100",
    elite_plus: "bg-gradient-to-r from-amber-500 to-rose-500 text-white",
    couple_bundle: "bg-pink-600 text-pink-100",
    admin: "bg-red-600 text-red-100",
  };
  
  return (
    <div className="flex items-center gap-2">
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${tierColors[tier] || tierColors.free}`}>
        {tier?.replace(/_/g, ' ').toUpperCase()}
      </span>
      {isActive !== undefined && (
        <span className={`text-xs ${isActive ? 'text-emerald-400' : 'text-rose-400'}`}>
          {isActive ? '● Active' : '○ Inactive'}
        </span>
      )}
    </div>
  );
}

/**
 * Main Debug Panel Component
 */
export default function EntitlementsDebugPanel() {
  const { user, token } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastBlockReason, setLastBlockReason] = useState(null);
  
  // Check if panel should be shown at all
  const shouldShow = PANEL_ALLOWED && user?.is_admin;
  
  // Load saved panel state
  useEffect(() => {
    try {
      const saved = localStorage.getItem(PANEL_STATE_KEY);
      if (saved) {
        const state = JSON.parse(saved);
        setIsOpen(state.isOpen ?? false);
        setIsMinimized(state.isMinimized ?? false);
      }
    } catch (e) {
      // Ignore parsing errors
    }
  }, []);
  
  // Save panel state
  useEffect(() => {
    try {
      localStorage.setItem(PANEL_STATE_KEY, JSON.stringify({ isOpen, isMinimized }));
    } catch (e) {
      // Ignore storage errors
    }
  }, [isOpen, isMinimized]);
  
  // Fetch entitlements
  const fetchEntitlements = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API}/api/me/entitlements`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      
      const data = await res.json();
      setEntitlements(data);
      
      // Check for last block reason in localStorage (if feature sets it)
      try {
        const blockReason = localStorage.getItem('last_entitlement_block');
        if (blockReason) {
          setLastBlockReason(JSON.parse(blockReason));
        }
      } catch (e) {
        // Ignore
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);
  
  // Auto-fetch when panel opens
  useEffect(() => {
    if (isOpen && !entitlements && !loading) {
      fetchEntitlements();
    }
  }, [isOpen, entitlements, loading, fetchEntitlements]);
  
  // Don't render anything if not allowed
  if (!shouldShow) {
    return null;
  }
  
  // Toggle button when panel is closed
  if (!isOpen) {
    return <PanelToggleButton onClick={() => setIsOpen(true)} isVisible={false} />;
  }
  
  // Minimized state - just show a small bar
  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-20 right-4 z-[10001] bg-slate-900 border border-slate-700 rounded-lg shadow-xl flex items-center gap-2 px-3 py-2"
        data-testid="entitlements-debug-panel-minimized"
      >
        <Bug className="w-4 h-4 text-amber-400" />
        <span className="text-xs text-slate-400">Entitlements</span>
        <button
          onClick={() => setIsMinimized(false)}
          className="text-slate-500 hover:text-slate-300 transition-colors"
          title="Expand"
        >
          <Maximize2 className="w-4 h-4" />
        </button>
        <button
          onClick={() => setIsOpen(false)}
          className="text-slate-500 hover:text-rose-400 transition-colors"
          title="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }
  
  return (
    <div 
      className="fixed bottom-20 right-4 z-[10001] w-80 max-h-[70vh] bg-slate-900 border border-slate-700 rounded-lg shadow-xl flex flex-col overflow-hidden"
      data-testid="entitlements-debug-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-slate-800 border-b border-slate-700">
        <div className="flex items-center gap-2">
          <Bug className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-medium text-slate-200">Entitlements Debug</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={fetchEntitlements}
            disabled={loading}
            className="p-1 text-slate-500 hover:text-slate-300 transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setIsMinimized(true)}
            className="p-1 text-slate-500 hover:text-slate-300 transition-colors"
            title="Minimize"
          >
            <Minimize2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 text-slate-500 hover:text-rose-400 transition-colors"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {error && (
          <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-2 text-xs text-rose-400">
            Error: {error}
          </div>
        )}
        
        {loading && !entitlements && (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 text-slate-500 animate-spin" />
          </div>
        )}
        
        {entitlements && (
          <>
            {/* User Info */}
            <Section icon={User} title="User Info" defaultOpen={true}>
              <div className="space-y-1">
                <div className="flex items-center justify-between py-1 px-2 rounded bg-slate-800/50">
                  <span className="text-xs text-slate-400">User ID</span>
                  <span className="text-xs font-mono text-slate-300 truncate max-w-[150px]" title={user?.id}>
                    {user?.id?.slice(0, 8)}...
                  </span>
                </div>
                <div className="flex items-center justify-between py-1 px-2 rounded bg-slate-800/50">
                  <span className="text-xs text-slate-400">Email</span>
                  <span className="text-xs text-slate-300 truncate max-w-[150px]" title={user?.email}>
                    {user?.email}
                  </span>
                </div>
                <div className="flex items-center justify-between py-1 px-2 rounded bg-slate-800/50">
                  <span className="text-xs text-slate-400">Tier</span>
                  <TierBadge 
                    tier={entitlements.effective_tier || entitlements.subscription?.tier} 
                    isActive={entitlements.subscription?.is_active}
                  />
                </div>
                <BoolIndicator label="Is Premium" value={entitlements.is_premium} />
                <BoolIndicator label="Is Admin" value={user?.is_admin} />
              </div>
            </Section>
            
            {/* Subscription */}
            <Section icon={Shield} title="Subscription" defaultOpen={false}>
              <div className="space-y-1">
                <NumericValue label="Source" value={entitlements.subscription?.source || "none"} />
                <BoolIndicator label="Active" value={entitlements.subscription?.is_active} />
                <BoolIndicator label="In Grace Period" value={entitlements.subscription?.in_grace_period} />
                <BoolIndicator label="Scheduled Cancel" value={entitlements.subscription?.scheduled_to_cancel} />
                {entitlements.subscription?.expires_at && (
                  <div className="flex items-center justify-between py-1 px-2 rounded bg-slate-800/50">
                    <span className="text-xs text-slate-400">Expires</span>
                    <span className="text-xs text-slate-300">
                      {new Date(entitlements.subscription.expires_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </Section>
            
            {/* Verification */}
            <Section icon={Shield} title="Verification" defaultOpen={false}>
              <div className="space-y-1">
                <BoolIndicator label="Identity Verified" value={entitlements.verification?.identity_verified} />
                <BoolIndicator label="Age Verified" value={entitlements.verification?.age_verified} />
                <BoolIndicator label="Email Verified" value={entitlements.verification?.email_verified} />
              </div>
            </Section>
            
            {/* Features (Boolean Flags) */}
            <Section icon={Settings} title="Features" defaultOpen={true}>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {entitlements.features && Object.entries(entitlements.features)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([key, value]) => (
                    <BoolIndicator key={key} label={key.replace(/_/g, ' ')} value={value} />
                  ))
                }
              </div>
            </Section>
            
            {/* Limits */}
            <Section icon={Gauge} title="Limits" defaultOpen={false}>
              <div className="space-y-1">
                {entitlements.limits && Object.entries(entitlements.limits)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([key, value]) => (
                    <NumericValue key={key} label={key.replace(/_/g, ' ')} value={value} />
                  ))
                }
                {entitlements.daily_lounge_minutes !== undefined && (
                  <NumericValue label="Daily Lounge Minutes (Legacy)" value={entitlements.daily_lounge_minutes} unit="min" />
                )}
                {entitlements.lounge_usage_seconds_today !== undefined && (
                  <NumericValue label="Lounge Used Today" value={Math.round(entitlements.lounge_usage_seconds_today / 60)} unit="min" />
                )}
              </div>
            </Section>
            
            {/* Thresholds */}
            <Section icon={Settings} title="Thresholds" defaultOpen={false}>
              <div className="space-y-1">
                {entitlements.thresholds && Object.entries(entitlements.thresholds)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([key, value]) => (
                    <NumericValue key={key} label={key.replace(/_/g, ' ')} value={value} />
                  ))
                }
              </div>
            </Section>
            
            {/* Requirements */}
            <Section icon={AlertTriangle} title="Requirements" defaultOpen={false}>
              <div className="space-y-1">
                {entitlements.requirements && Object.entries(entitlements.requirements)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([key, value]) => (
                    <BoolIndicator key={key} label={key.replace(/_/g, ' ')} value={value} />
                  ))
                }
              </div>
            </Section>
            
            {/* Last Block Reason */}
            {lastBlockReason && (
              <Section icon={AlertTriangle} title="Last Block Reason" defaultOpen={true}>
                <div className="space-y-1">
                  <div className="bg-rose-500/10 border border-rose-500/30 rounded p-2">
                    <div className="text-xs text-rose-400 font-medium">
                      {lastBlockReason.reason_code || "UNKNOWN"}
                    </div>
                    {lastBlockReason.required_feature && (
                      <div className="text-xs text-slate-400 mt-1">
                        Required: {lastBlockReason.required_feature}
                      </div>
                    )}
                    {lastBlockReason.message && (
                      <div className="text-xs text-slate-500 mt-1">
                        {lastBlockReason.message}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      localStorage.removeItem('last_entitlement_block');
                      setLastBlockReason(null);
                    }}
                    className="text-xs text-slate-500 hover:text-slate-300"
                  >
                    Clear
                  </button>
                </div>
              </Section>
            )}
            
            {/* Metadata */}
            <div className="pt-2 border-t border-slate-800">
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>v{entitlements.version}</span>
                <span>{new Date(entitlements.server_timestamp).toLocaleTimeString()}</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Hook to check if debug panel should be visible
 */
export function useEntitlementsDebugAllowed() {
  const { user } = useContext(AuthContext);
  return PANEL_ALLOWED && user?.is_admin;
}
